//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, CircularProgress, Grid } from '@mui/material'
import React, { useState } from 'react'

import { PoolOffchainDataCTO, PoolOffchainDataSchema } from '@shared/PoolOffchainDataCTO'

import { onPromise } from '@react/components/UiHelpers'
import { SectionHeader } from '@react/components/common/SectionHeader'
import EditField from '@react/components/elements/EditField'
import DisplayNameField from '@react/components/elements/DisplayNameField'
import { useOtChainState, useOtSelector } from '@react/redux/store'

import { yupResolver } from '@hookform/resolvers/yup'

import { SubmitHandler, useForm } from 'react-hook-form'

import ViewField from '@react/components/elements/ViewField'

import { poolOffchainDataPut } from '@react/fetchers/PoolFetchers'
import { PoolTypeEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'

export default function PoolOffchainDataForm(props: { poolOffchainDataCTO: PoolOffchainDataCTO }): React.ReactElement {
  const { poolOffchainDataCTO: initialValues } = props
  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[initialValues?.poolAddrOffchain ?? ETH_ADDR_ZERO]
  )
  const {
    chainConfiguration: { chainConfigurationName },
  } = useOtChainState()
  const poolType = poolOverviewCTO?.poolType
  const [loading, setLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const {
    cognitoUser,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()

  logOT.info('PoolOffchainDataForm initialValues', initialValues)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PoolOffchainDataCTO>({
    defaultValues: initialValues,
    resolver: yupResolver(PoolOffchainDataSchema),
  })

  const onErrors = (errors: any) => {
    logOT.info('ERRORS: ', errors)
  }

  const onSubmit: SubmitHandler<PoolOffchainDataCTO> = async (poolFormData: PoolOffchainDataCTO): Promise<void> => {
    logOT.info('onSubmit', poolFormData)
    logOT.info('onSubmit displayName', poolFormData.displayName)
    setLoading(true)
    logOT.info('poolFormData', poolFormData, process.env.stage)
    if (serverApiBaseUrl && (cognitoUser || chainConfigurationName === 'Hardhat')) {
      setIsSubmitted(true)

      await poolOffchainDataPut(serverApiBaseUrl, restAPIKey, poolFormData, cognitoUser!)
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    }
  }
  logOT.info('PoolOffchainDataForm initialValues', initialValues)
  return (
    <Box>
      <form onSubmit={onPromise(handleSubmit(onSubmit, onErrors))}>
        <SectionHeader>Vault Offchain Data</SectionHeader>
        <Grid container spacing={1} sx={{ marginTop: 4 }}>
          <Grid item xs={12} sm={12}>
            <ViewField
              dataObject={initialValues}
              fieldSchema={PoolOffchainDataSchema.describe().fields.poolAddrOffchain}
            ></ViewField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <DisplayNameField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolOffchainDataSchema.describe().fields.displayName}
              poolName={poolOverviewCTO?.name}
            />
          </Grid>
          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.benchmarkRate}
              ></EditField>
            </Grid>
          )}
          <Grid item sm={6}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolOffchainDataSchema.describe().fields.advisoryFeeRate}
            ></EditField>
          </Grid>
          <Grid item sm={6}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolOffchainDataSchema.describe().fields.platformFeeRate}
            ></EditField>
          </Grid>
          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.liquidityFeeRate}
              ></EditField>
            </Grid>
          )}

          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.contractualCurrency}
              ></EditField>
            </Grid>
          )}

          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.minimumTransferAmount}
              ></EditField>
            </Grid>
          )}
          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.maximumTransferAmount}
              ></EditField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Dynamic && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.transferInDays}
              ></EditField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Dynamic && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.transferOutDays}
              ></EditField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Term && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.earlyWithdrawalProcessingPeriod}
              ></EditField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Term && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                setValue={setValue}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.automaticRollover}
              ></EditField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Term && (
            <Grid item sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolOffchainDataSchema.describe().fields.rolloverCollateral}
              ></EditField>
            </Grid>
          )}

          <Grid item sm={6}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolOffchainDataSchema.describe().fields.yieldType}
            ></EditField>
          </Grid>

          <Grid item sm={6}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolOffchainDataSchema.describe().fields.loanType}
            ></EditField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              disabled={isSubmitted}
              type="submit"
              variant="contained"
              sx={{ marginBottom: 4 }}
              onClick={() => handleSubmit(onSubmit, onErrors)}
            >
              {loading && <CircularProgress />}
              Save
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}
